/**
 * AdminFeeManagement — Vinci Admin console tab.
 *
 * Live, admin-editable control over the Director Induction Fee
 * (default ₹2,50,000). Any change here cascades to:
 *   · Razorpay order creation (next director-fee payment link uses new amount)
 *   · Director invitation + apply emails (quotes the new amount)
 *   · Vinci Master Ledger — every change appears as a `policy` row
 *   · Compliance Log — `admin.director_fee_changed` event captured
 */
import { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  BadgeIndianRupee, Coins, History, RefreshCw, Save, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Info,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export function AdminFeeManagement() {
  const { api } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/config/director-fee");
      setState(data);
      setAmount(String(data.current_fee_inr));
    } catch {
      toast.error("Could not load fee state.");
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const isChanging = useMemo(() => {
    const n = Number(amount);
    return state && Number.isFinite(n) && n > 0 && n !== state.current_fee_inr;
  }, [amount, state]);

  const delta = useMemo(() => {
    if (!state) return 0;
    return Number(amount || 0) - Number(state.current_fee_inr || 0);
  }, [amount, state]);

  const save = async () => {
    const n = Number(amount);
    if (!Number.isInteger(n) || n < 1000) { toast.error("Enter a whole rupee amount ≥ ₹1,000."); return; }
    if (confirm.trim().toUpperCase() !== "CONFIRM") { toast.error("Type CONFIRM to authorise the change."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/config/director-fee",
        { amount: n, note: note || "" });
      toast.success(data.changed
        ? `Fee updated · ${fmtInr(data.previous_fee_inr)} → ${fmtInr(data.current_fee_inr)}`
        : "No change — new amount equals current fee.");
      setNote(""); setConfirm(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally { setSaving(false); }
  };

  if (loading || !state) {
    return <div className="font-body text-sm text-[#8A8A82]" data-testid="fee-mgmt-loading">Loading fee state…</div>;
  }

  return (
    <div data-testid="admin-fee-management">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Fee Management</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0]">Director Authorization Fee</h2>
          <p className="font-body text-[13px] text-[#8A8A82] mt-1 max-w-2xl">
            Executive-flexible pricing for the Level-0 Executive Board seat.
            Every change flows live into Razorpay orders, invitation emails,
            and the Vinci Master Ledger.
          </p>
        </div>
        <button onClick={load} data-testid="fee-refresh"
          className="rounded-sm border border-[#C9A961]/40 text-[#C9A961] px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em]">
          <RefreshCw size={12} className="inline mr-1 -mt-0.5" />Refresh
        </button>
      </div>

      {/* Live fee card */}
      <div className="rounded-sm border border-[#C9A961]/40 bg-gradient-to-br from-[#1a1207] via-[#0b0908] to-[#0f0d0a] p-6 mb-6" data-testid="fee-live-card">
        <div className="flex items-center gap-3">
          <BadgeIndianRupee size={28} className="text-[#C9A961]" />
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Currently in force</div>
        </div>
        <div className="mt-3 font-serif text-5xl md:text-6xl text-[#F5F5F0] tabular-nums" data-testid="fee-current">
          {fmtInr(state.current_fee_inr)}
        </div>
        <div className="font-body text-[12px] text-[#8A8A82] mt-2">
          Default (env fallback): <b className="text-[#C9C9C2]">{fmtInr(state.default_fee_inr)}</b>
          {state.last_updated_at && (
            <> · Last updated {state.last_updated_at.slice(0, 19)} UTC by <b className="text-[#C9C9C2]">{state.last_updated_by || "—"}</b></>
          )}
          {state.last_note && <> · Note: <i>{state.last_note}</i></>}
        </div>
      </div>

      {/* Editor */}
      <div className="rounded-sm border border-[#C9A961]/25 bg-black/40 p-6 mb-6" data-testid="fee-editor">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">New fee (INR)</label>
            <input type="number" min={state.min} max={state.max} step="1"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="fee-input"
              className="w-full mt-1 bg-black border border-[#C9A961]/40 rounded-sm px-3 py-2 font-mono text-lg tabular-nums text-[#F5F5F0]" />
            <div className="font-body text-[10px] text-[#8A8A82] mt-1">
              Bounds: {fmtInr(state.min)} – {fmtInr(state.max)}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Note for the ledger (optional but recommended)</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={280}
              placeholder="e.g. Founder-strategic discount for Q1-2026 partners"
              data-testid="fee-note"
              className="w-full mt-1 bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
          </div>
        </div>

        {isChanging && (
          <div className="mt-4 rounded-sm border border-[#F5D67A]/40 bg-[#F5D67A]/[0.06] p-4 flex items-start gap-3" data-testid="fee-diff-callout">
            {delta > 0
              ? <ArrowUpRight className="text-[#F5D67A] shrink-0 mt-0.5" size={18} />
              : <ArrowDownRight className="text-[#2FB999] shrink-0 mt-0.5" size={18} />}
            <div className="font-body text-sm text-[#F5F5F0]">
              You are about to <b>{delta > 0 ? "raise" : "lower"}</b> the Director Fee from{" "}
              <b className="tabular-nums">{fmtInr(state.current_fee_inr)}</b> to{" "}
              <b className="tabular-nums">{fmtInr(Number(amount))}</b>{" "}
              (<span style={{ color: delta > 0 ? "#F5D67A" : "#2FB999" }}>{delta > 0 ? "+" : ""}{fmtInr(delta)}</span>).
              This affects <b>the next Razorpay order and next invitation email</b> — existing applicants keep their locked-in price.
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type CONFIRM to authorise"
            data-testid="fee-confirm"
            disabled={!isChanging}
            className="bg-black border border-[#C0392B]/40 rounded-sm px-3 py-2 font-mono text-sm tracking-widest text-[#F5F5F0] uppercase disabled:opacity-40 w-64" />
          <button onClick={save} disabled={saving || !isChanging || confirm.trim().toUpperCase() !== "CONFIRM"}
            data-testid="fee-save"
            className="rounded-sm bg-[#C9A961] hover:bg-[#F5D67A] text-black px-6 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.28em] transition-colors disabled:opacity-40">
            <Save size={13} className="inline mr-2 -mt-0.5" />{saving ? "Saving…" : "Commit fee change"}
          </button>
          <span className="font-body text-[11px] text-[#8A8A82] inline-flex items-center gap-1.5">
            <Info size={11} /> Change is logged to the Master Ledger + Compliance Log
          </span>
        </div>
      </div>

      {/* History */}
      <div className="rounded-sm border border-[#C9A961]/20 bg-black/40 p-6" data-testid="fee-history">
        <div className="flex items-center gap-2 mb-3">
          <History size={16} className="text-[#C9A961]" />
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Change history</div>
        </div>
        {(state.history || []).length === 0 ? (
          <div className="font-body text-sm text-[#8A8A82]">No changes yet — the fee has been at the default since inception.</div>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-white/5">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-black/60 font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">
                  <th className="px-3 py-2">When (UTC)</th>
                  <th className="px-3 py-2">Fee set to</th>
                  <th className="px-3 py-2">Previous</th>
                  <th className="px-3 py-2">Delta</th>
                  <th className="px-3 py-2">Admin</th>
                  <th className="px-3 py-2">Note</th>
                </tr>
              </thead>
              <tbody className="font-body text-[12px] text-[#F5F5F0]">
                {state.history.map((h) => (
                  <tr key={h.id} data-testid={`fee-history-row-${h.id}`} className="border-t border-white/5">
                    <td className="px-3 py-2 whitespace-nowrap text-[#C9C9C2] tabular-nums">{(h.at || "").slice(0, 19)}</td>
                    <td className="px-3 py-2 tabular-nums font-bold">{fmtInr(h.amount)}</td>
                    <td className="px-3 py-2 tabular-nums text-[#8A8A82]">{fmtInr(h.previous_amount)}</td>
                    <td className="px-3 py-2 tabular-nums"
                      style={{ color: (h.delta || 0) > 0 ? "#F5D67A" : "#2FB999" }}>
                      {(h.delta || 0) > 0 ? "+" : ""}{fmtInr(h.delta)}
                    </td>
                    <td className="px-3 py-2">{h.updated_by || "—"}</td>
                    <td className="px-3 py-2 text-[#8A8A82] italic">{h.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
